import axios from 'axios';
import axiosInstance from '../axiosInstance';

const TOKEN_HEADER = 'x-portal-token';
export const PORTAL_EVENT_HEADER = 'x-portal-event';

// PortalGate owns the route's active event. Keeping it in this module lets
// every scoring call send the event header without threading an event id
// through each page component.
let activePortalEventId = '';

export const setActivePortalEvent = (eventId) => {
  activePortalEventId = eventId ? String(eventId) : '';
};

// A dedicated instance: the app-wide axios instance sends every 401 to
// /signin, which would throw a club official out of the portal and into a
// BowlScout login page they cannot use.
export const portalRequest = axios.create({
  baseURL: '/api/portal',
  headers: { 'Content-Type': 'application/json' }
});

const hasPortalToken = (config) =>
  Boolean(config?.headers && config.headers[TOKEN_HEADER]);

portalRequest.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      hasPortalToken(error.config) &&
      !error.config?.silentExpiry
    ) {
      window.dispatchEvent(new CustomEvent('portal-session-expired'));
    }
    return Promise.reject(error);
  }
);

export const portalSessionKey = ({ eventId, kind, venueId }) =>
  kind === 'secretary'
    ? `portalSession:${eventId}:secretary`
    : `portalSession:${eventId}:club:${venueId}`;

export const portalAccessSessionKey = (accessId) =>
  `portalSession:access:${accessId}`;

export const readPortalToken = (key) => {
  try {
    return window.localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

export const writePortalToken = (key, token) => {
  try {
    window.localStorage.setItem(key, token);
  } catch {
    // Private browsing can refuse writes. The in-memory token still works
    // until the page is closed.
  }
};

export const clearPortalToken = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
};

const request = async (config) => {
  const response = await portalRequest.request(config);
  return response.data;
};

const tokenConfig = (token, { eventId, silentExpiry, ...extra } = {}) => {
  const headers = { ...(extra.headers || {}), [TOKEN_HEADER]: token };
  const selectedEvent = eventId || activePortalEventId;

  if (selectedEvent) {
    headers[PORTAL_EVENT_HEADER] = String(selectedEvent);
  }

  return {
    ...extra,
    ...(silentExpiry ? { silentExpiry: true } : {}),
    headers
  };
};

export const getInfo = ({ eventId, kind, venueId }) =>
  request({
    method: 'get',
    url: '/info',
    params: { eventId, kind, ...(venueId ? { venueId } : {}) }
  });

export const login = ({ eventId, kind, venueId, pin, email = '' }) =>
  request({
    method: 'post',
    url: '/login',
    data: {
      eventId,
      kind,
      ...(venueId ? { venueId } : {}),
      pin,
      ...(email ? { email } : {})
    }
  });

export const logout = (token) =>
  request({ method: 'post', url: '/logout', ...tokenConfig(token) });

export const getSession = (token, eventId, { silentExpiry = false } = {}) =>
  request({
    method: 'get',
    url: '/session',
    ...tokenConfig(token, { eventId, silentExpiry })
  });

export const getQrCodes = (token) =>
  request({ method: 'get', url: '/qr-codes', ...tokenConfig(token) });

export const getDrawExport = (token, { scope = 'club', venueId } = {}) =>
  request({
    method: 'get',
    url: '/draw',
    params: { scope, ...(venueId ? { venueId } : {}) },
    ...tokenConfig(token)
  });

export const getEventLogs = (token) =>
  request({ method: 'get', url: '/logs', ...tokenConfig(token) });

/**
 * Record a browser-performed portal activity (QR print, log or draw export).
 * Fire-and-forget: a failed audit write must never block the action itself.
 */
export const reportPortalActivity = (token, body = {}, { eventId = '' } = {}) => {
  if (!token || !body.operation) return;
  portalRequest
    .post('/activity', body, tokenConfig(token, { eventId, silentExpiry: true }))
    .catch(() => null);
};

export const getSections = (token) =>
  request({ method: 'get', url: '/sections', ...tokenConfig(token) });

export const getSectionGames = (token, sectionId) =>
  request({
    method: 'get',
    url: `/sections/${encodeURIComponent(sectionId)}/games`,
    ...tokenConfig(token)
  });

export const getSectionStandings = (token, sectionId) =>
  request({
    method: 'get',
    url: `/sections/${encodeURIComponent(sectionId)}/standings`,
    ...tokenConfig(token)
  });

export const getClubs = (token) =>
  request({ method: 'get', url: '/clubs', ...tokenConfig(token) });

export const getClubSections = (token, venueId) =>
  request({
    method: 'get',
    url: `/clubs/${encodeURIComponent(venueId)}/sections`,
    ...tokenConfig(token)
  });

export const getClubSectionGames = (token, venueId, sectionId) =>
  request({
    method: 'get',
    url: `/clubs/${encodeURIComponent(venueId)}/sections/${encodeURIComponent(sectionId)}/games`,
    ...tokenConfig(token)
  });

export const getClubSectionStandings = (token, venueId, sectionId) =>
  request({
    method: 'get',
    url: `/clubs/${encodeURIComponent(venueId)}/sections/${encodeURIComponent(sectionId)}/standings`,
    ...tokenConfig(token)
  });

export const getGame = (token, gameId) =>
  request({
    method: 'get',
    url: `/games/${encodeURIComponent(gameId)}`,
    ...tokenConfig(token)
  });

export const startGame = (token, gameId, data = {}) =>
  request({
    method: 'post',
    url: `/games/${encodeURIComponent(gameId)}/start`,
    data,
    ...tokenConfig(token)
  });

export const scoreGame = (token, gameId, data) =>
  request({
    method: 'post',
    url: `/games/${encodeURIComponent(gameId)}/scores`,
    data,
    ...tokenConfig(token)
  });

export const inputScore = (token, gameId, data) =>
  request({
    method: 'put',
    url: `/games/${encodeURIComponent(gameId)}/score-input`,
    data,
    ...tokenConfig(token)
  });

export const saveScorecardPhoto = (token, gameId, image) =>
  request({
    method: 'put',
    url: `/games/${encodeURIComponent(gameId)}/photo`,
    data: { image },
    ...tokenConfig(token)
  });

export const undoScore = (token, gameId) =>
  request({
    method: 'post',
    url: `/games/${encodeURIComponent(gameId)}/undo`,
    ...tokenConfig(token)
  });

export const startNewSet = (token, gameId) =>
  request({
    method: 'post',
    url: `/games/${encodeURIComponent(gameId)}/new-set`,
    ...tokenConfig(token)
  });

export const startPlayoff = (token, gameId) =>
  request({
    method: 'post',
    url: `/games/${encodeURIComponent(gameId)}/playoff/start`,
    ...tokenConfig(token)
  });

export const scorePlayoff = (token, gameId, teamId) =>
  request({
    method: 'post',
    url: `/games/${encodeURIComponent(gameId)}/playoff/scores`,
    data: { teamId },
    ...tokenConfig(token)
  });

export const undoPlayoff = (token, gameId) =>
  request({
    method: 'post',
    url: `/games/${encodeURIComponent(gameId)}/playoff/undo`,
    ...tokenConfig(token)
  });

// Secretary-session management: clubs only. The server refuses any attempt to
// touch the secretary PIN.
export const getSecretaryManagedAccess = (token) =>
  request({ method: 'get', url: '/secretary/access', ...tokenConfig(token) });

export const generateManagedClubPin = (token, venueId) =>
  request({
    method: 'post',
    url: `/secretary/access/club/${encodeURIComponent(venueId)}/pin`,
    ...tokenConfig(token)
  });

export const getManagedClubPin = (token, venueId) =>
  request({
    method: 'get',
    url: `/secretary/access/club/${encodeURIComponent(venueId)}/pin`,
    ...tokenConfig(token)
  });

export const disableManagedClub = (token, venueId) =>
  request({
    method: 'delete',
    url: `/secretary/access/club/${encodeURIComponent(venueId)}`,
    ...tokenConfig(token)
  });

export const sendManagedClubEmail = (token, venueId, data) =>
  request({
    method: 'post',
    url: `/secretary/access/club/${encodeURIComponent(venueId)}/email`,
    data,
    ...tokenConfig(token)
  });

export const portalErrorMessage = (error, fallback = 'Something went wrong. Please try again.') =>
  error?.response?.data?.message || error?.message || fallback;

// Organiser endpoints. These are normal BowlScout API calls: the organiser is
// signed in, and the server enforces Admin on the event's workspace.
export const getPortalAccess = (eventId) =>
  axiosInstance
    .get(`/portal/admin/${encodeURIComponent(eventId)}/access`)
    .then((response) => response.data);

export const generateClubPortalPin = (eventId, venueId) =>
  axiosInstance
    .post(`/portal/admin/${encodeURIComponent(eventId)}/access/club/${encodeURIComponent(venueId)}/pin`)
    .then((response) => response.data);

export const getClubPortalPin = (eventId, venueId) =>
  axiosInstance
    .get(`/portal/admin/${encodeURIComponent(eventId)}/access/club/${encodeURIComponent(venueId)}/pin`)
    .then((response) => response.data);

export const generateSecretaryPortalPin = (eventId) =>
  axiosInstance
    .post(`/portal/admin/${encodeURIComponent(eventId)}/access/secretary/pin`)
    .then((response) => response.data);

export const getSecretaryPortalPin = (eventId) =>
  axiosInstance
    .get(`/portal/admin/${encodeURIComponent(eventId)}/access/secretary/pin`)
    .then((response) => response.data);

export const disableClubPortal = (eventId, venueId) =>
  axiosInstance
    .delete(
      `/portal/admin/${encodeURIComponent(eventId)}/access/club/${encodeURIComponent(venueId)}`
    )
    .then((response) => response.data);

export const disableSecretaryPortal = (eventId) =>
  axiosInstance
    .delete(`/portal/admin/${encodeURIComponent(eventId)}/access/secretary`)
    .then((response) => response.data);

export const sendClubPortalEmail = (eventId, venueId, data) =>
  axiosInstance
    .post(
      `/portal/admin/${encodeURIComponent(eventId)}/access/club/${encodeURIComponent(venueId)}/email`,
      data
    )
    .then((response) => response.data);

export const sendSecretaryPortalEmail = (eventId, data) =>
  axiosInstance
    .post(`/portal/admin/${encodeURIComponent(eventId)}/access/secretary/email`, data)
    .then((response) => response.data);

export const getClubPortalContacts = (eventId, venueId) =>
  axiosInstance
    .get(
      `/portal/admin/${encodeURIComponent(eventId)}/access/club/${encodeURIComponent(venueId)}/contacts`
    )
    .then((response) => response.data);

export const addClubPortalContact = (eventId, venueId, data) =>
  axiosInstance
    .post(
      `/portal/admin/${encodeURIComponent(eventId)}/access/club/${encodeURIComponent(venueId)}/contacts`,
      data
    )
    .then((response) => response.data);

export const whatsappClubPortalContact = (eventId, venueId, contactId, data = {}) =>
  axiosInstance
    .post(
      `/portal/admin/${encodeURIComponent(eventId)}/access/club/${encodeURIComponent(venueId)}/contacts/${encodeURIComponent(contactId)}/whatsapp`,
      data
    )
    .then((response) => response.data);

export const removeClubPortalContact = (eventId, venueId, contactId) =>
  axiosInstance
    .delete(
      `/portal/admin/${encodeURIComponent(eventId)}/access/club/${encodeURIComponent(venueId)}/contacts/${encodeURIComponent(contactId)}`
    )
    .then((response) => response.data);

export const getSecretaryPortalContacts = (eventId) =>
  axiosInstance
    .get(`/portal/admin/${encodeURIComponent(eventId)}/access/secretary/contacts`)
    .then((response) => response.data);

export const addSecretaryPortalContact = (eventId, data) =>
  axiosInstance
    .post(
      `/portal/admin/${encodeURIComponent(eventId)}/access/secretary/contacts`,
      data
    )
    .then((response) => response.data);

export const whatsappSecretaryPortalContact = (eventId, contactId, data = {}) =>
  axiosInstance
    .post(
      `/portal/admin/${encodeURIComponent(eventId)}/access/secretary/contacts/${encodeURIComponent(contactId)}/whatsapp`,
      data
    )
    .then((response) => response.data);

export const removeSecretaryPortalContact = (eventId, contactId) =>
  axiosInstance
    .delete(
      `/portal/admin/${encodeURIComponent(eventId)}/access/secretary/contacts/${encodeURIComponent(contactId)}`
    )
    .then((response) => response.data);

export const sendBulkPortalEmails = (eventId, data) =>
  axiosInstance
    .post(
      `/portal/admin/${encodeURIComponent(eventId)}/access/email/bulk`,
      data
    )
    .then((response) => response.data);

export const getManagedClubContacts = (token, venueId) =>
  request({
    method: 'get',
    url: `/secretary/access/club/${encodeURIComponent(venueId)}/contacts`,
    ...tokenConfig(token)
  });

export const addManagedClubContact = (token, venueId, data) =>
  request({
    method: 'post',
    url: `/secretary/access/club/${encodeURIComponent(venueId)}/contacts`,
    data,
    ...tokenConfig(token)
  });

export const removeManagedClubContact = (token, venueId, contactId) =>
  request({
    method: 'delete',
    url: `/secretary/access/club/${encodeURIComponent(venueId)}/contacts/${encodeURIComponent(contactId)}`,
    ...tokenConfig(token)
  });

export const whatsappManagedClubContact = (token, venueId, contactId, data = {}) =>
  request({
    method: 'post',
    url: `/secretary/access/club/${encodeURIComponent(venueId)}/contacts/${encodeURIComponent(contactId)}/whatsapp`,
    data,
    ...tokenConfig(token)
  });

export const sendManagedClubBulkEmails = (token, data) =>
  request({
    method: 'post',
    url: '/secretary/access/email/bulk',
    data,
    ...tokenConfig(token)
  });
